import { apiClient } from '@/shared/api';
import type { ApiResponse, Pagination } from '@/shared/types/api';
import type {
  FinancialReportsResponse,
  ReportDetailsResponse,
  ReportType,
  ReportTimeRange,
} from '../types/report.types';

// Types for financial data
export interface FinancialMetric {
  label: string;
  value: string | number;
  change: string;
}

export interface FinancialOverviewResponse {
  timeRange: number;
  metrics: {
    earnings: FinancialMetric;
    activeResidents: FinancialMetric;
    outstandingBalance: FinancialMetric;
    outstandingFeesCollected: FinancialMetric;
    customChargesAdded: FinancialMetric;
  };
}

export interface ResidentFinancialHistory {
  residentProfileId: string;
  residentName: string;
  residentEmail: string;
  houseName: string;
  programFees: number;
  customCharges: number;
  outstandingFee: number;
  totalDue: number;
  paidAmount: number;
  lastPayment: string | null;
  status: string;
}

export interface ResidentFinancialHistoryResponse {
  reports: ResidentFinancialHistory[];
  pagination: Pagination;
}

// API functions
export const financialApi = {
  // Get financial overview
  getOverview: async (
    range: number,
  ): Promise<ApiResponse<FinancialOverviewResponse>> => {
    const response = await apiClient.get('/financials/overview', {
      params: { range },
    });
    return response.data;
  },

  // Get resident financial history
  getResidentFinancialHistory: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    residentName?: string;
    status?: string;
  }): Promise<ApiResponse<ResidentFinancialHistoryResponse>> => {
    const response = await apiClient.get('/financials', { params });
    return response.data;
  },

  // Get single resident financial details
  getResidentFinancialDetails: async (
    residentId: string,
  ): Promise<ApiResponse<ResidentFinancialDetails>> => {
    const response = await apiClient.get(
      `/financials/resident/${residentId}/history`,
    );
    return response.data;
  },

  // Get financial reports
  getFinancialReports: async (params?: {
    type?: ReportType;
    timeRange?: ReportTimeRange;
  }): Promise<ApiResponse<FinancialReportsResponse>> => {
    const response = await apiClient.get('/reports', { params });
    return response.data;
  },

  // Get report details by ID
  getReportDetails: async (
    reportId: string,
  ): Promise<ApiResponse<ReportDetailsResponse>> => {
    const response = await apiClient.get(`/reports/${reportId}`);
    return response.data;
  },
};

export interface ResidentFinancialDetails {
  residentId: string;
  name: string;
  email: string;
  moveInDate: string | null;
  bedNo: string | null;
  totalAmountPaid: number;
  lastPaidDate: string | null;
  outstandingBalance: number;
  status: 'Paid' | 'Pending';
  house: {
    id: string;
    name: string;
    category: string | null;
    image: string | null;
    manager: {
      name: string;
      email: string;
    } | null;
  } | null;
  paymentHistory: {
    invoiceId: string;
    amount: number;
    month: string;
    dueDate: string;
    paidAt: string | null;
    status: 'PENDING' | 'PAID' | 'OVERDUE' | 'CANCELLED';
    paymentMethod: string | null;
    downloadUrl: string;
  }[];
}
